import cloneDeep from 'lodash/cloneDeep';

import { ActionTree, MutationTree } from 'vuex';
import { RootState } from '@/store/types';
import { ServerResult } from '@/core/http/server_result';
import { fetchEnumsFromSchema } from '@/core/tools/forms/fetch_enums_from_schema';
import { generateFormObject } from '@/core/tools/forms/generate_form_object';
import { prepareFormObjectForSave } from '@/core/tools/forms/prepare_form_object_for_save';
import moment from 'moment';

interface ProducedProductFormState {
    loaded: boolean;
    loading: boolean;
    fatalError: boolean;

    document: any;
    form: any;
    schema: any;
    ingredient_schema: any;
    list: any[];
    ingredients: any[];
    products: any[];

    meta: any;
}

const state: ProducedProductFormState = {
    loading: false,
    loaded: false,
    fatalError: false,

    document: null,
    form: null,
    schema: null,
    ingredient_schema: null,
    list: [],
    ingredients: [],
    products: [],

    meta: {},
};

const initState: ProducedProductFormState = cloneDeep(state);

export const PRODUCED_PRODUCT_DATA = 'GET_PRODUCED_PRODUCT_DATA';
export const PRODUCED_PRODUCT_LIST = 'PRODUCED_PRODUCT_LIST';
export const PRODUCTS_LIST = 'PRODUCTS_LIST';
export const INGREDIENTS_LIST = 'INGREDIENTS_LIST';
export const INGREDIENTS_SCHEMA = 'INGREDIENTS_SCHEMA';
export const PRODUCED_PRODUCT_SCHEMA = 'GET_PRODUCED_PRODUCT_SCHEMA';

function formatIngredients(items: any) {
    return items.map((item: any) => {
        item.value_time_1 = parseFloat(item.value_time_1 || 0)
        item.value_time_2 = parseFloat(item.value_time_2 || 0)
        item.value_time_3 = parseFloat(item.value_time_3 || 0)
        item.value_time_4 = parseFloat(item.value_time_4 || 0)
        item.value_time_5 = parseFloat(item.value_time_5 || 0)
        item.value_time_6 = parseFloat(item.value_time_6 || 0)
        item.value_time_7 = parseFloat(item.value_time_7 || 0)
        return item
    })
}

export const actions: ActionTree<ProducedProductFormState, RootState> = {
    reset({ commit }) {
        commit('RESET_STATE')
    },

    init({ commit, dispatch }, params) {
        return dispatch('loadData', params);
    },

    loadData({ commit }, params) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            vm.$db([
                vm.$q(PRODUCED_PRODUCT_DATA)
                    .raw('documents.get')
                    .action({
                        id: params.id
                    }),
                vm.$q(PRODUCED_PRODUCT_LIST)
                    .raw('documents.list')
                    .action({
                        "columns": {
                            "id": "pp.id",
                            "name": "pp.name",
                        },
                        "from": "s:produced_products",
                        "from_as": "pp",
                        "where": {
                            $not:[{$eq: ["pp.id", {$: params.id}]}]
                        }
                    }),
                vm.$q(INGREDIENTS_LIST)
                    .raw('documents.list')
                    .action({
                        "columns": {
                            "id": "ppi.id",
                            "ingredient_id": "vc.id",
                            "produced_product_id": "ppi.produced_product_id",
                            "name": "vc.name",
                            "type": "vc.type",
                            "value_time_1": "ppi.value_time_1",
                            "value_time_2": "ppi.value_time_2",
                            "value_time_3": "ppi.value_time_3",
                            "value_time_4": "ppi.value_time_4",
                            "value_time_5": "ppi.value_time_5",
                            "value_time_6": "ppi.value_time_6",
                            "value_time_7": "ppi.value_time_7",
                        },
                        "from": "s:voc_ingredients",
                        "from_as": "vc",
                        "joins": [
                            {
                                "join": "s:produced_product_ingredients",
                                "type": 'left',
                                "as": "ppi",
                                "on": {
                                    $and: [
                                        {"$eq": ['ppi.ingredient_id', 'vc.id']},
                                        {"$eq": ['ppi.produced_product_id', {$: params.id}]}
                                    ]
                                }
                            }
                        ],
                        "page_size": 1000,
                        "where": {
                            $eq: ['vc.is_atom', {$: true}]
                        }
                    }),
                vm.$q(PRODUCTS_LIST)
                    .raw('documents.list')
                    .action({
                        "columns": {
                            "id": "ppp.id",
                            "product_id": "p.id",
                            "label": "p.name",
                        },
                        "from": "s:products",
                        "from_as": "p",
                        "joins": [
                            {
                                "join": "s:produced_product_products",
                                // "type": '',
                                "as": "ppp",
                                "on": {
                                    $and: [
                                        {"$eq": ['ppp.product_id', 'p.id']},
                                        {"$eq": ['ppp.produced_product_id', {$: params.id}]}
                                    ]
                                }
                            }
                        ],
                        "page_size": 1000,
                    }),
                vm.$q(INGREDIENTS_SCHEMA)
                    .raw('schemas.get')
                    .action({
                        id: `s:produced_product_ingredients`
                    }),
            ]).then((result: ServerResult) => {
                commit('SET_FORM_DATA', {
                    ...result.getOperation(PRODUCED_PRODUCT_DATA).getContent(),
                    list: result.getOperation(PRODUCED_PRODUCT_LIST).getContent().documents,
                    products: formatIngredients(result.getOperation(PRODUCTS_LIST).getContent().documents),
                    ingredients: formatIngredients(result.getOperation(INGREDIENTS_LIST).getContent().documents),
                    ingredients_schema: result.getOperation(INGREDIENTS_SCHEMA).getContent().schema,
                });

                commit('LOADED_SUCCESS');
                resolve(null);
            })
            .catch((error: any) => {
                console.log('error', error)
                commit('LOADED_ERROR');
                reject()
            })
        })
    },

    prepareNew({ commit, state }, initParams) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            vm.$db([
                vm.$q(PRODUCED_PRODUCT_SCHEMA)
                    .raw('schemas.get')
                    .action({
                        id: `s:produced_products`
                    }),
                    vm.$q(PRODUCED_PRODUCT_LIST)
                        .raw('documents.list')
                        .action({
                            "columns": {
                                "id": "pp.id",
                                "name": "pp.name",
                            },
                            "from": "s:produced_products",
                            "from_as": "pp"
                        }),
                    vm.$q(INGREDIENTS_LIST)
                        .raw('documents.list')
                        .action({
                            "columns": {
                                "ingredient_id": "vc.id",
                                "name": "vc.name",
                                "type": "vc.type",
                            },
                            "from": "s:voc_ingredients",
                            "from_as": "vc",
                            "where": {
                                $eq: ['vc.is_vdp', {$: true}]
                            }
                        }),
                    vm.$q(INGREDIENTS_SCHEMA)
                        .raw('schemas.get')
                        .action({
                            id: `s:produced_product_ingredients`
                        }),
            ]).then((result: ServerResult) => {
                commit('GENERATE_FORM_DATA', {
                    ...result.getOperation(PRODUCED_PRODUCT_SCHEMA).getContent(),
                    initParams,
                    list: result.getOperation(PRODUCED_PRODUCT_LIST).getContent().documents,
                    ingredients: formatIngredients(result.getOperation(INGREDIENTS_LIST).getContent().documents),
                    ingredients_schema: result.getOperation(INGREDIENTS_SCHEMA).getContent().schema,
                });

                commit('LOADED_SUCCESS');
                resolve(null);
            })
            .catch((error: any) => {
                console.log('error', error)
                commit('LOADED_ERROR');
                reject()
            })
        })
    },

    saveForm({ commit }, documentId) {
        const vm = (this as any)._vm;

        const normalizedForm = prepareFormObjectForSave(state.schema, state.form);

        // Тип продукта необязателен: передаём значение как есть (в т.ч. null),
        // чтобы сохранялись и выбор типа, и его очистка
        if (state.form && 'product_type_id' in state.form) {
            normalizedForm.product_type_id = state.form.product_type_id || null;
        }

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            let saveQuery = null;
            state.ingredients = state.ingredients.map((item: any) => {
                return prepareFormObjectForSave(state.ingredient_schema, item)
            })

            if (documentId) {
                saveQuery = vm.$q('PRODUCED_PRODUCT_DATA_SAVE')
                    .update(documentId, normalizedForm)
            } else {
                saveQuery = vm.$q('PRODUCED_PRODUCT_DATA_SAVE')
                    .create('s:produced_products', normalizedForm)
            }

            vm.$db([
                saveQuery
            ]).then((result: ServerResult) => {
                const operationResult = result.getOperation('PRODUCED_PRODUCT_DATA_SAVE');

                if (operationResult.hasError()) {
                    console.log('error', operationResult.getContent());

                    commit('LOADED_SUCCESS');
                    reject();
                } else {
                    const form =operationResult.getContent()
                    
                    vm.$db([
                        saveQuery = vm.$q('PRODUCED_PRODUCT_DATA_SAVE')
                            .update(form.document.id, {})
                            .createManyRelation('produced_product_ingredients', 'produced_product_id', (createRelated: any) => {
                                return state.ingredients.filter((item: any) => !item.id || (typeof(item.id) !== 'string')).map((item, index) => createRelated(item));
                            })
                            .updateManyRelation('produced_product_ingredients', 'produced_product_id', (updateRelated: any) => {
                                return state.ingredients.filter((item: any) => (typeof(item.id) == 'string')).map((item, index) => updateRelated(item))
                            })
                            .createManyRelation('produced_product_products', 'produced_product_id', (createRelated: any) => {
                                return state.products.filter((item: any) => !item.id || (typeof(item.id) !== 'string')).map((item, index) => createRelated({product_id: item.product_id}));
                            })
                            .updateManyRelation('produced_product_products', 'produced_product_id', (updateRelated: any) => {
                                return state.products.filter((item: any) => (typeof(item.id) == 'string')).map((item, index) => updateRelated({id: item.id, product_id: item.product_id}))
                            })
                    ]).then((result: ServerResult) => {
                        commit('ON_FORM_SAVE', form)
                        commit('LOADED_SUCCESS');
                        resolve(form);
                    })
                }
            })
            .catch((error: any) => {
                console.error('errror', error)
                reject(error);
            });
        })
    },

    setArchive({state, commit}) {
        return new Promise((resolve, reject) => {
            commit('START_LOADING');
            const vm = (this as any)._vm;

            const normalizedForm = prepareFormObjectForSave(state.schema, {
                archived_at: moment().toISOString()
            });
            console.log(normalizedForm)

            const saveQuery = vm.$q('SAVE')
                .update(state.form.id, normalizedForm)
            vm.$db([
                saveQuery
            ]).then(((result: ServerResult) => {
                console.log(result)
                commit('LOADED_SUCCESS');
                resolve();
            }));
        })
    },

    setUnarchive({state, commit}) {
        return new Promise((resolve, reject) => {
            commit('START_LOADING');
            const vm = (this as any)._vm;

            const normalizedForm = prepareFormObjectForSave(state.schema, {
                archived_at: null
            });
            normalizedForm['archived_at'] = null;

            const saveQuery = vm.$q('SAVE')
                .update(state.form.id, normalizedForm)
            vm.$db([
                saveQuery
            ]).then(((result: ServerResult) => {
                console.log(result)
                commit('LOADED_SUCCESS');
                resolve();
            }));
        })
    },

    changeFormValue({commit}, {field, newValue}) {
        commit('CHANGE_VALUE', {field, newValue});
    },

    changeIngredientValue({commit}, {idx, field, newValue}) {
        commit('CHANGE_INGREDIENT_VALUE', {idx, field, newValue});
    },
    addProduct({commit}, {product}) {
        if (product) {
            commit('ADD_PRODUCT', {product});
        }
    },
    delProduct({commit}, {product}) {
        if (product) {
            commit('DEL_PRODUCT', {product});
        }
    },

};

export const mutations: MutationTree<ProducedProductFormState> = {
    RESET_STATE: (state) => {
        for(const key in initState) {
            (state as any)[key] = (initState as any)[key]
        }
    },

    START_LOADING: (state) => {
        state.loaded = false;
        state.loading = true;
        state.fatalError = false;
    },

    LOADED_SUCCESS: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = false;
    },

    LOADED_ERROR: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = true;
    },

    SET_PRODUCED_PRODUCT_DATA(state, {document, list}) {
        state.document = document;
    },

    ADD_PRODUCT(state, {product}) {
        state.products.push({
            product_id: product.id,
            label: product.label,
        });
    },

    DEL_PRODUCT(state, {product}) {
        state.products.splice(state.products.indexOf(product), 1);
    },

    SET_FORM_DATA(state, {document, schema, list, products, ingredients, ingredients_schema}) {
        state.document = document;
        state.schema = schema;
        state.meta = fetchEnumsFromSchema(schema);

        if (!state.document.remainders) {
            state.document.remainders = {data: {}}
        }
        console.log('------>', list)
        list.map((item: any) => {
            if (!state.document.remainders.data[item.id]) {
                state.document.remainders.data[item.id] = {
                    id: item.id,
                    name: item.name,
                    value: 0
                }
            }
        })
        state.form = generateFormObject(schema, state.document);
        state.products = products
        state.ingredients = ingredients
        state.ingredient_schema = ingredients_schema
    },

    GENERATE_FORM_DATA(state, {schema, initParams, list, ingredients, ingredients_schema}) {
        state.document = {};
        state.schema = schema;
        state.meta = fetchEnumsFromSchema(schema);

        if (!initParams.remainders) {
            initParams.remainders = {data: {}}
        }
        list.map((item: any) => {
            console.log('------>', item)
            if (!initParams.remainders.data[item.id]) {
                initParams.remainders.data[item.id] = {
                    id: item.id,
                    name: item.name,
                    value: 0
                }
            }
        })
        state.form = generateFormObject(schema, initParams);
        state.products = []
        state.ingredients = ingredients
        state.ingredient_schema = ingredients_schema
    },

    ON_FORM_SAVE(state, {document, schema}) {
        state.document = document;
        state.schema = schema;

        state.form = generateFormObject(schema, state.document);
    },

    CHANGE_VALUE(state: ProducedProductFormState, {field, newValue}) {
        switch (field) {
            default:
                state.form[field] = newValue;
        }
    },

    CHANGE_INGREDIENT_VALUE(state: ProducedProductFormState, {idx, field, newValue}) {
        switch (field) {
            default:
                state.ingredients[idx][field] = newValue;
        }
    },
};

const getters = {
    fields: (state: ProducedProductFormState) => {
        return state.schema.fields.reduce((prev: any, field: any) => {
            prev[field.code] = field;
            return prev;
        }, {});
    },
};

export const STORE_KEY = '$_corrector_manager_form';

export const store = {
    namespaced: true,
    state,
    actions,
    getters,
    mutations,
};
